import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { find } from "lodash/fp";

import {
  getWorkspaceUserInvite,
  getWorkspaceUserInvites,
  updateWorkspaceUserInvite,
} from "@app/common/account/api";
import useAuth from "@app/common/auth/useAuth";

const useWorkspaceInvite = (inviteId?: string | null) => {
  const queryClient = useQueryClient();
  const { user, fetchAndSetUser } = useAuth();

  const inviteMutation = useMutation<
    void,
    unknown,
    { inviteId: string; isAccepted: boolean }
  >({
    mutationFn: updateWorkspaceUserInvite,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaceInvite"] });
    },
    onSuccess: async (_data, { isAccepted }) => {
      if (!isAccepted) return;
      try {
        // if the user just joined a workspace, refetch the user's access
        // permissions, billing account, etc.
        await fetchAndSetUser();
        // the workspace and billing queries should refresh *after* we've
        // refreshed the user, since they're going to depend on the new
        // values on the user object (e.g., updated billing account id)
        queryClient.invalidateQueries({ queryKey: ["workspace"] });
        queryClient.invalidateQueries({ queryKey: ["billing"] });
      } catch (e) {
        Sentry.captureException(e);
      }
    },
  });

  const inviteQuery = useQuery({
    queryKey: ["workspaceInvite", inviteId],
    queryFn: () => getWorkspaceUserInvite(inviteId!),
    enabled: Boolean(inviteId),
    staleTime: 5 * 60 * 1000,
  });

  const invitesQuery = useQuery({
    queryKey: ["workspaceInvite", "all"],
    queryFn: () => getWorkspaceUserInvites(),
    enabled: !inviteId && Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const invites = invitesQuery.data ?? [];
  const invite = (inviteId ? inviteQuery.data : invites[0]) ?? null;
  const pendingInvite = find({ status: "pending" }, invites) ?? null;

  return {
    ...(inviteId ? inviteQuery : invitesQuery),
    hasInvite: Boolean(inviteId) || Boolean(invite),
    invite,
    invites,
    pendingInvite,
    updateInvite: inviteMutation.mutate,
    isLoading: invitesQuery.isLoading || inviteQuery.isLoading,
  };
};

export default useWorkspaceInvite;
